/**
 * Detecção de pitch por NSDF (Normalized Square Difference Function),
 * com interpolação parabólica — família de algoritmos tipo YIN.
 * Para performance móvel, a correlação é limitada à faixa de pitch relevante.
 */
import { clamp, freqToNote } from '../utils/misc.js';

export interface PitchOptions {
  windowMs?: number;
  hopMs?: number;
  minFreq?: number;
  maxFreq?: number;
  threshold?: number;
  gateDb?: number;
}

export interface PitchResult {
  time: number;
  freq: number | null;
  confidence: number;
  rms: number;
  note?: string;
  cents?: number;
}

/** NSDF até `maxLag` (O(N × maxLag), não O(N²) em toda a janela). */
export function nsdf(frame: Float32Array, maxLag = Math.floor(frame.length / 2)): Float32Array {
  const n = frame.length;
  const limit = Math.min(n - 2, Math.max(2, maxLag));
  const out = new Float32Array(limit + 2);
  for (let tau = 0; tau <= limit + 1; tau++) {
    const count = n - tau;
    let corr = 0;
    let energy = 0;
    for (let j = 0; j < count; j++) {
      const a = frame[j];
      const b = frame[j + tau];
      corr += a * b;
      energy += a * a + b * b;
    }
    out[tau] = energy > 1e-12 ? (2 * corr) / energy : 0;
  }
  return out;
}

export function pickNsdfPeak(
  nsdfArr: Float32Array,
  sampleRate: number,
  minFreq: number,
  maxFreq: number,
  threshold = 0.6
): { freq: number; confidence: number } | null {
  const tauMin = Math.max(2, Math.floor(sampleRate / maxFreq));
  const tauMax = Math.min(nsdfArr.length - 2, Math.ceil(sampleRate / minFreq));
  if (tauMax <= tauMin + 2) return null;

  let start = tauMin;
  for (let t = tauMin; t < tauMax; t++) {
    if (nsdfArr[t] < 0) { start = t; break; }
  }
  let bestTau = -1;
  let bestVal = -Infinity;
  const firstCandidate = Math.max(start, tauMin);
  // Escolhe o primeiro pico local confiável, como no YIN/CMNDF. Buscar só o
  // maior valor da janela tende a escolher múltiplos do período (sub-harmônicos).
  for (let t = firstCandidate + 1; t < tauMax; t++) {
    const v = nsdfArr[t];
    if (v >= nsdfArr[t - 1] && v > nsdfArr[t + 1] && v >= threshold) {
      bestTau = t;
      bestVal = v;
      break;
    }
  }
  // Se não há pico acima do threshold, usa o melhor disponível e deixa a
  // confiança abaixo do limiar para que analyzePitch possa marcá-lo como incerto.
  if (bestTau < 0) {
    for (let t = firstCandidate; t <= tauMax; t++) {
      const v = nsdfArr[t];
      if (v > bestVal) { bestVal = v; bestTau = t; }
    }
  }
  if (bestTau < 0 || bestVal <= 0) return null;

  const y0 = nsdfArr[bestTau - 1] ?? bestVal;
  const y1 = bestVal;
  const y2 = nsdfArr[bestTau + 1] ?? bestVal;
  const denom = y0 - 2 * y1 + y2;
  const shift = Math.abs(denom) > 1e-9 ? (0.5 * (y0 - y2)) / denom : 0;
  const tau = bestTau + clamp(shift, -1, 1);
  const freq = sampleRate / tau;
  if (freq < minFreq * 0.9 || freq > maxFreq * 1.1) return null;
  return { freq, confidence: clamp(y1, 0, 1) };
}

/** Reduz sample rate por seleção de amostras — suficiente para pitch vocal. */
function decimate(input: Float32Array, factor: number): Float32Array {
  if (factor <= 1) return input;
  const out = new Float32Array(Math.ceil(input.length / factor));
  for (let i = 0; i < out.length; i++) out[i] = input[i * factor];
  return out;
}

export function analyzePitch(
  source: Float32Array,
  sampleRate: number,
  opts: PitchOptions = {}
): PitchResult[] {
  const windowMs = opts.windowMs ?? 50;
  const hopMs = opts.hopMs ?? 40;
  const minFreq = opts.minFreq ?? 70;
  const maxFreq = opts.maxFreq ?? 1000;
  const threshold = opts.threshold ?? 0.6;
  const gateDb = opts.gateDb ?? -48;
  const gate = Math.pow(10, gateDb / 20);

  // Pitch vocal fica abaixo de 1 kHz. 12 kHz preserva bem a fundamental,
  // reduzindo o trabalho em ~4× em gravações de 48 kHz.
  const factor = sampleRate > 24000 ? Math.max(1, Math.floor(sampleRate / 12000)) : 1;
  const data = factor > 1 ? decimate(source, factor) : source;
  const sr = sampleRate / factor;
  const win = Math.max(128, Math.round((sr * windowMs) / 1000));
  const hop = Math.max(64, Math.round((sr * hopMs) / 1000));
  const maxLag = Math.min(win - 3, Math.ceil(sr / minFreq) + 2);
  const out: PitchResult[] = [];
  const frame = new Float32Array(win);

  for (let i = 0; i + win <= data.length; i += hop) {
    let sum = 0;
    for (let j = 0; j < win; j++) {
      const x = data[i + j];
      frame[j] = x;
      sum += x * x;
    }
    const r = Math.sqrt(sum / win);
    const time = (i + win / 2) / sr;
    if (r < gate) {
      out.push({ time, freq: null, confidence: 0, rms: r });
      continue;
    }
    let mean = 0;
    for (let j = 0; j < win; j++) mean += frame[j];
    mean /= win;
    for (let j = 0; j < win; j++) frame[j] -= mean;

    const ns = nsdf(frame, maxLag);
    const p = pickNsdfPeak(ns, sr, minFreq, maxFreq, threshold);
    if (p && p.confidence >= threshold) {
      const n = freqToNote(p.freq);
      out.push({ time, freq: p.freq, confidence: p.confidence, rms: r, note: n?.note, cents: n?.cents });
    } else {
      out.push({ time, freq: null, confidence: p?.confidence ?? 0, rms: r });
    }
  }
  return out;
}

export function pitchStabilityScore(frames: PitchResult[]): {
  score: number;
  medianAbsCents: number;
  stdCents: number;
  voicedRatio: number;
  notes: { note: string; count: number }[];
} {
  const voiced = frames.filter((f) => f.freq && typeof f.cents === 'number');
  const cents = voiced.map((f) => f.cents as number);
  const voicedRatio = frames.length ? cents.length / frames.length : 0;
  if (cents.length < 8) return { score: 0, medianAbsCents: 0, stdCents: 0, voicedRatio, notes: [] };
  const abs = cents.map((c) => Math.abs(c)).sort((a, b) => a - b);
  const medAbs = abs.length % 2 ? abs[abs.length >> 1] : (abs[(abs.length >> 1) - 1] + abs[abs.length >> 1]) / 2;
  const mean = cents.reduce((a, b) => a + b, 0) / cents.length;
  const std = Math.sqrt(cents.reduce((a, b) => a + (b - mean) * (b - mean), 0) / cents.length);
  const score = clamp(Math.round(100 * (1 - medAbs / 50)), 0, 100);
  const hist = new Map<string, number>();
  for (const f of frames) if (f.note) hist.set(f.note, (hist.get(f.note) ?? 0) + 1);
  const notes = [...hist.entries()].map(([note, count]) => ({ note, count })).sort((a, b) => b.count - a.count);
  return { score, medianAbsCents: medAbs, stdCents: std, voicedRatio, notes };
}

export function estimateKey(frames: PitchResult[]): string | undefined {
  const hist = new Map<number, number>();
  for (const f of frames) {
    if (!f.freq) continue;
    const n = freqToNote(f.freq);
    if (!n) continue;
    const pc = ((n.midi % 12) + 12) % 12;
    hist.set(pc, (hist.get(pc) ?? 0) + f.confidence);
  }
  if (!hist.size) return undefined;
  const major = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
  const minor = [6.33, 2.68, 3.52, 5.38, 2.6, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];
  const names = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  let best = { score: -Infinity, label: '' };
  for (let shift = 0; shift < 12; shift++) {
    let cMaj = 0, cMin = 0;
    for (let pc = 0; pc < 12; pc++) {
      const w = hist.get((pc + shift) % 12) ?? 0;
      cMaj += w * major[pc];
      cMin += w * minor[pc];
    }
    if (cMaj > best.score) best = { score: cMaj, label: `${names[shift]} maior` };
    if (cMin > best.score) best = { score: cMin, label: `${names[shift]} menor` };
  }
  return best.label || undefined;
}
